// The agentic chat segment-builder (#10242 Phase 1).
//
// An assistant turn is modeled as an ordered list of Segments — one per thing
// the agent did, in the order it did it: a thinking block, a narration text
// block, a tool call, a chart. This replaces the legacy "one bubble per turn
// with an overwritten content string + a side toolSteps array" shape, so the UI
// can render a Claude-style timeline where each step is its own div.
//
// `applyChatUpdate` is a pure fold step over the same `ChatUpdate` events the
// websocket emits live AND the (seq-ordered, delta-free) events the backfill
// returns on reload — so one builder reconstructs a turn identically in both
// cases. Transient streaming/thinking deltas only show up live; on reload only
// the durable finals + tool events replay, which is why the open-segment logic
// keys off "is the trailing segment still streaming" rather than off the deltas.
//
// The "done" of a turn is tracked at the TURN level (`status`), driven only by a
// terminal status/error event — never by a text block finalizing. That is the
// structural fix for the bug where a mid-turn final text made the whole turn
// look finished while it was still running.

import type {
  ChatUpdate,
  DashboardBuildEnd,
  RefusedAgentFile,
  TurnNoticeCode,
} from "../ChatBackendInterface";
import {
  artifactIdFromPreview,
  isCreateTableTool,
  isEditorDocumentTool,
  isUpdateEditorDocumentTool,
  workspaceIdFromPreview,
} from "../editorDocument";
import type { ChatCitationSource } from "../types";
import { opsResultOf } from "../../RMS/Dashboards/opsAnswer";
import { CitedBlock, EvidenceSource, collectSources } from "../evidenceFooter";
import {
  parseTaskToolResult,
  taskToolKind,
  type TaskToolKind,
  type TaskToolResult,
} from "../taskTools";

export type SegmentStatus = "streaming" | "complete" | "error";
export type ToolStatus = "running" | "done" | "error";
export type TurnStatus = "running" | "completed" | "failed" | "canceled";

export interface ThinkingSegment {
  kind: "thinking";
  id: string;
  text: string;
  status: SegmentStatus;
}

// One persisted, sanitized excerpt of a completed thinking run (#10396) —
// the durable counterpart of the transient thinking deltas. Renders as a
// clock-icon note row inside its work block, live and on reload alike.
export interface NoteSegment {
  kind: "note";
  id: string;
  text: string;
}

export interface TextSegment {
  kind: "text";
  id: string;
  text: string;
  status: SegmentStatus;
  // Legacy citations for this text block, keyed by the bracketed [NNNNN.M]
  // marker, resolved against the renderer.
  chatSourceMap?: Record<string, ChatCitationSource[]>;
  // The persisted answer row this block came from. An agent answer's map
  // carries most passages only on request, by this id (#12081).
  eventId?: string;
}

export interface ToolSegment {
  kind: "tool";
  id: string;
  toolCallId: string;
  toolName: string;
  input?: Record<string, unknown>;
  status: ToolStatus;
  outputPreview?: string;
  error?: string;
  durationMs?: number;
  // Backend-resolved readable label naming the document/entity acted on;
  // preferred over deriveToolLabel when set. Undefined for unenriched tools.
  // (#10373)
  displayVerb?: string;
  // A dispatched subagent's own segments (its tool calls + composed answer)
  // nest here, keyed off `parent_tool_call_id`, so the subagent's work renders
  // as its own nested timeline under this node (#10467). [] for a plain tool.
  children: Segment[];
}

// The live editor document a create_editor_document call opened (#11548
// r20): stands in the transcript like other artifacts so the user can click
// back into it after closing the side panel. Appended pending on the
// tool_call; the workspace id arrives on the successful tool_result (an
// errored or id-less result removes it — nothing to reopen).
export interface DocumentSegment {
  kind: "document";
  id: string;
  toolCallId: string;
  workspaceId: string | null;
  title: string | null;
  pending: boolean;
}

// A live table a create_table_artifact call registered (r78): stands in
// the transcript like a document so the user can click back into it after
// closing the side panel — a DRAFT table is otherwise reachable nowhere.
// Appended pending on the tool_call; the artifact id arrives on the
// successful tool_result (an errored or id-less result removes it).
export interface TableArtifactSegment {
  kind: "tableArtifact";
  id: string;
  toolCallId: string;
  artifactId: string | null;
  title: string | null;
  pending: boolean;
}

// The task card a create_task / update_task / delete_task call answers
// with (#11942, CD ruling 6): the transcript's one task card, in the state
// the tool left the task in. Appended pending on the tool_call; the result's
// preview resolves it (an errored result, or one naming no task, removes it —
// nothing was done, so there is nothing to show or undo).
export interface TaskSegment {
  kind: "task";
  id: string;
  toolCallId: string;
  tool: Exclude<TaskToolKind, "list">;
  // The title on the tool call's input — the stand-in when the result's
  // preview was cut before the title.
  inputTitle: string | null;
  result: TaskToolResult | null;
  pending: boolean;
}

// The handoff turn (#11942 round 4, CD ruling 4): when the agent closes one
// of its own tasks and the next task is a person's, the transcript says so
// in a card — which task, whose, and that the run pauses here. Appended
// pending on the complete_task tool_call; the result's data resolves it (a
// result whose next task is the agent's own removes it: nothing was handed
// over).
export interface HandoffSegment {
  kind: "handoff";
  id: string;
  toolCallId: string;
  result: HandoffResult | null;
  pending: boolean;
}

export interface HandoffResult {
  /** The task the agent just finished. */
  doneTitle: string | null;
  /** The person's task that opened. */
  taskId: string;
  taskTitle: string;
  assigneeName: string | null;
  action: "approve" | "mark_done";
  stepPosition: number | null;
  listId: string | null;
}

// An email the agent asked to send (#12210): the card that stands in the
// transcript where the send_email call sits. Appended pending on the
// tool_call with the request as the input spelled it (`to` is user ids, team
// ids or addresses; `body` is markdown); the tool_result says whether the
// call made a DRAFT the person must confirm (`draftId`) or QUEUED the email
// for the turn's end (an automation's run), and which addresses it resolved.
// `cc` and `bcc` are the copied and blind-copied people (#12381): as the input
// named them, then as the result resolved them.
// An errored result — a refusal — removes it: nothing was drafted.
// `refused` is who the result left off the email, and why: users of the
// organization who cannot open a document it carries (#14002).
export interface EmailRefusal {
  email: string;
  reason: string;
}

export interface EmailSegment {
  kind: "email";
  id: string;
  toolCallId: string;
  to: string[];
  subject: string;
  body: string;
  attachments: string[];
  draftId: string | null;
  queued: boolean;
  recipients: string[];
  cc: string[];
  bcc: string[];
  refused: EmailRefusal[];
  pending: boolean;
}

// The dashboard assistant's tools (#13380), rendered by name as the email
// card is: a change to the editor's draft stands as its outcome line with an
// Undo; a library search as the widgets it found, each with Add; a widget
// build as the line naming what is being built. Each appears pending on its
// tool_call and resolves on a successful result (an errored one removes it).
export type DashboardToolKind = "ops" | "search" | "build";

export interface DashboardWidgetMatch {
  id: string;
  name: string;
  description: string;
  scope: string;
  root_kind: string | null;
  current_revision_id: string;
}

/**
 * What a change's answer says the server did (dashboard-assistant-polish-3 requirement 5): how
 * many ops applied, why each declined one was not done, and the widgets an applied filter could
 * not be checked against. Null when the answer carried no structured data to read.
 */
export interface DashboardOpsOutcome {
  applied: number;
  declined: string[];
  /** The ids of the widgets an applied filter may not narrow until they render. */
  unchecked: string[];
  /** The ids of the tabs a filter was set on that hold no widget yet: nothing there reads it. */
  emptyTabs: string[];
  /** The names of library widgets placed with a look of their own (#13714 L1.6). */
  fromLibrary?: string[];
}

export interface DashboardToolSegment {
  kind: "dashboard";
  id: string;
  toolCallId: string;
  tool: DashboardToolKind;
  /** The outcome line ("Placed 6 widgets on Overview"), or the build's title. */
  text: string;
  /** An ops call's answer, read; null for the other tools and for an answer with no data. */
  outcome: DashboardOpsOutcome | null;
  /** The search's term, for Browse all. */
  term: string;
  matches: DashboardWidgetMatch[];
  /** A build's id, from its request's answer, to match its ending. */
  buildId: string | null;
  /** How the build ended, once it has; null while it builds. */
  ended: DashboardBuildEnd | null;
  /**
   * The ending came after the turn that asked for the build had ended (dashboard-assistant-polish
   * requirement 10): the transcript shows it as its own entry at its end, and this card says only
   * how the build ended.
   */
  endedAfterTurn: boolean;
  /** A build that revises a widget already placed (`request_widget_revision`), not a new one. */
  revision: boolean;
  pending: boolean;
}

const DASHBOARD_TOOLS: Readonly<Record<string, DashboardToolKind>> = {
  apply_dashboard_ops: "ops",
  search_widget_library: "search",
  request_widget_build: "build",
  request_widget_revision: "build",
  request_widget_copy: "build",
};

/** A tool's name without the hosted MCP server's namespace (`mcp__finpilot__name`). */
export const bareToolName = (name: string): string =>
  name.startsWith("mcp__") ? name.split("__").slice(2).join("__") : name;

/** Which dashboard assistant tool a call is, by its name with or without the MCP namespace. */
export const dashboardToolKind = (
  toolName: string | undefined
): DashboardToolKind | null =>
  toolName ? (DASHBOARD_TOOLS[bareToolName(toolName)] ?? null) : null;

// The dashboard assistant's reads (dashboard-assistant-polish-3 requirement 7): what they read
// is on the editor's screen already, so like the carded tools they fold into the trace with no
// row of their own.
const DASHBOARD_READ_TOOLS: ReadonlySet<string> = new Set([
  "get_dashboard_draft",
  "get_widget_builds",
  "get_rms_kind_fields",
]);

/** Whether a call is one of the dashboard assistant's reads, which show no tool row. */
export const isDashboardReadTool = (toolName: string | undefined): boolean =>
  toolName !== undefined && DASHBOARD_READ_TOOLS.has(bareToolName(toolName));

const textOf = (value: unknown): string =>
  typeof value === "string" ? value : "";

/** A result's preview, unless it is the tool's JSON (which has no words to show). */
const previewWords = (preview: string | undefined): string => {
  const text = (preview ?? "").trim();
  return text.startsWith("{") || text.startsWith("[") ? "" : text;
};

const matchesOf = (data: unknown): DashboardWidgetMatch[] => {
  const matches =
    data && typeof data === "object" && !Array.isArray(data)
      ? (data as { matches?: unknown }).matches
      : undefined;
  if (!Array.isArray(matches)) return [];
  return matches
    .filter(
      (m): m is Record<string, unknown> =>
        typeof m === "object" &&
        m !== null &&
        typeof (m as { id?: unknown }).id === "string" &&
        typeof (m as { name?: unknown }).name === "string"
    )
    .slice(0, 6)
    .map((m) => ({
      id: String(m.id),
      name: String(m.name),
      description: textOf(m.description),
      scope: textOf(m.scope),
      root_kind: typeof m.root_kind === "string" ? m.root_kind : null,
      current_revision_id: textOf(m.current_revision_id),
    }));
};

/** The outcome line of a change: what the call said it did, else a count of what it applied. */
const opsOutcome = (
  input: Record<string, unknown> | undefined,
  preview: string | undefined,
  data: unknown
): string => {
  const summary = textOf(input?.summary).trim();
  if (summary) return summary;
  const words = previewWords(preview);
  if (words) return words;
  const applied =
    data && typeof data === "object" && !Array.isArray(data)
      ? (data as { applied?: unknown }).applied
      : undefined;
  const count = Array.isArray(applied) ? applied.length : 0;
  return count === 1 ? "Made 1 change" : `Made ${count} changes`;
};

/**
 * What an ops answer says the server did: the count applied, the reasons of the ops it declined,
 * and the widgets an applied filter could not be checked against, read by the editor's own parser
 * of the answer. Null without the answer's data, which the card shows as the summary alone, with
 * no check and no Undo.
 */
const opsAnswer = (data: unknown): DashboardOpsOutcome | null => {
  const answer = opsResultOf(data);
  if (!answer) return null;
  return {
    applied: answer.applied.length,
    declined: answer.declined.map((d) => d.reason),
    unchecked: [...new Set(answer.unchecked.flatMap((u) => u.widget_ids))],
    emptyTabs: [
      ...new Set(
        answer.unchecked.flatMap((u) =>
          u.widget_ids.length === 0 && u.tab_id ? [u.tab_id] : []
        )
      ),
    ],
    ...(answer.from_library
      ? { fromLibrary: answer.from_library.map((p) => p.name) }
      : {}),
  };
};

export interface ChartSegment {
  kind: "chart";
  id: string;
  chartId: string;
  spec: Record<string, unknown>;
  title?: string;
  sourceIds?: string[];
  // The chart is appended the moment its create_chart tool_call streams in, but
  // must not PAINT until its paired tool_result confirms success — a call the
  // tool body rejected would otherwise flash before the errored result removes
  // it, and the model's retry would leave a duplicate (#10501). True until the
  // result clears it; the render layer skips a pending chart.
  pending: boolean;
}

export interface DiagramSegment {
  kind: "diagram";
  id: string;
  diagramId: string;
  diagram: string;
  title?: string;
  sourceIds?: string[];
  // Deferred until its create_diagram tool_result confirms success — see
  // ChartSegment.pending (#10501).
  pending: boolean;
}

/** The agent paused the turn to ask the user something (#11155). Mirrors
 * `ClarifyingQuestionPayload` in `unified_events.py`. Ordered with the rest of
 * the turn's work, because where the agent stopped to ask is part of what it
 * did. `eventId` is the persisted row the answer is posted against — the only
 * segment that needs its event's identity, because it is the only one the
 * reader can act on. */
export interface ClarifyingQuestionSegment {
  kind: "clarifyingQuestion";
  id: string;
  eventId: string;
  question: string;
  // The SDK tool call that asked; null for a prose-era question.
  toolUseId: string | null;
}

/** Files this turn produced, rendered as download chips (#10492). */
export interface OutputsSegment {
  kind: "outputs";
  id: string;
  conversationId: string;
  turnId: string;
  files: string[];
}

/** Memory changed for this turn (#12402): one notice line naming what the
 *  memory is about, from a memory tool mid-turn or the post-turn pass. */
export interface MemoryUpdatedSegment {
  kind: "memoryUpdated";
  id: string;
  subjects: string[];
  /** A named agent's brief and skill files the turn saved (#11402). */
  files: string[];
  /** Those the writeback refused to save, with the reason (#11402). */
  refused: RefusedAgentFile[];
}

/** A builder turn's quiet status line (#11849): another pass started, or no
 *  pass worked. It stands where it arrived, between the replies. */
export interface TurnNoticeSegment {
  kind: "turnNotice";
  id: string;
  code: TurnNoticeCode;
}

export type Segment =
  | ThinkingSegment
  | NoteSegment
  | TextSegment
  | ToolSegment
  | DocumentSegment
  | TableArtifactSegment
  | TaskSegment
  | HandoffSegment
  | EmailSegment
  | DashboardToolSegment
  | ChartSegment
  | DiagramSegment
  | ClarifyingQuestionSegment
  | OutputsSegment
  | MemoryUpdatedSegment
  | TurnNoticeSegment;

export interface TurnState {
  status: TurnStatus;
  segments: Segment[];
  // Work-block trace state (#10382). A block is a contiguous run of tool
  // calls + thinking between two model text outputs; blocks are numbered in
  // order and both sides derive the same numbering. `blockHeaders` holds the
  // latest model-written header per block; `blockActivity` accumulates each
  // block's per-tool activity lines (deduped). Rebuilt identically on reload
  // from the persisted status_gist events.
  blockHeaders: Record<number, string>;
  blockActivity: Record<number, string[]>;
  // The message from the error that ended the turn, when one did. Turn-level
  // rather than a segment: the turn failed, it did not do a failing thing, and
  // `status` alone cannot say what went wrong. Mirrors `ErrorPayload.message`
  // in `unified_events.py`; undefined on a turn that did not fail.
  errorMessage?: string;
}

export const emptyTurn = (): TurnState => ({
  status: "running",
  segments: [],
  blockHeaders: {},
  blockActivity: {},
});

// Distinct sources the turn cited, for the answer's evidence footer (#11426):
// its count and its list. A subagent's nested text segments count toward the
// turn. Identity and order are collectSources' — first-cited, keyed on the
// underlying document/record, so repeated markers against one doc appear once.
export const collectSegmentSources = (
  segments: Segment[]
): EvidenceSource[] => {
  const blocks: CitedBlock[] = [];
  const visit = (list: Segment[]): void => {
    for (const segment of list) {
      if (segment.kind === "text")
        blocks.push({ text: segment.text, map: segment.chatSourceMap });
      if (segment.kind === "tool") visit(segment.children);
    }
  };
  visit(segments);
  return collectSources(blocks);
};

export const collectTurnSources = (turn: TurnState): EvidenceSource[] =>
  collectSegmentSources(turn.segments);

/** Every text segment's map folded into one lookup (#11975) — what a chart
 *  or diagram segment resolves its markers against. First map wins for a
 *  marker two segments both carry. */
export const segmentSourceMap = (
  segments: Segment[]
): Record<string, ChatCitationSource[]> => {
  const out: Record<string, ChatCitationSource[]> = {};
  const visit = (list: Segment[]): void => {
    for (const segment of list) {
      if (segment.kind === "text" && segment.chatSourceMap) {
        for (const [marker, entries] of Object.entries(segment.chatSourceMap)) {
          if (!(marker in out)) out[marker] = entries;
        }
      }
      if (segment.kind === "tool") visit(segment.children);
    }
  };
  visit(segments);
  return out;
};

// The trailing segment, when it is a still-streaming text/thinking block that
// further deltas (or a finalizing event) should flow into. Anything else means
// the next text/thinking starts a fresh segment.
const openSegment = (
  segments: Segment[]
): TextSegment | ThinkingSegment | null => {
  const last = segments[segments.length - 1];
  if (
    last &&
    (last.kind === "text" || last.kind === "thinking") &&
    last.status === "streaming"
  ) {
    return last;
  }
  return null;
};

// Finalize a trailing streaming text/thinking block (status -> complete). A
// no-op when the last segment isn't an open block. Used whenever a new kind of
// segment begins or the turn ends.
const closeOpen = (segments: Segment[]): Segment[] => {
  const open = openSegment(segments);
  if (!open) return segments;
  return segments.map((s) =>
    s === open ? { ...s, status: "complete" as const } : s
  );
};

const replaceLast = (segments: Segment[], next: Segment): Segment[] => {
  const copy = segments.slice();
  copy[copy.length - 1] = next;
  return copy;
};

// The agent's email tool (#12210), as the hosted MCP server namespaces it.
export const SEND_EMAIL_TOOL_NAME = "mcp__finpilot__send_email";
export const isSendEmailTool = (toolName: string | undefined): boolean =>
  toolName === SEND_EMAIL_TOOL_NAME || toolName === "send_email";

const stringList = (value: unknown): string[] =>
  Array.isArray(value)
    ? value.filter((v): v is string => typeof v === "string")
    : [];

// What a send_email tool_result says, or null when the text is not the tool's
// JSON (an errored call carries no result; a preview cut short does not
// parse). Read from the full content when the frame carries it, since the
// preview may be truncated; on reload only the preview survives.
interface SendEmailResult {
  queued: boolean;
  draftId: string | null;
  recipients: string[];
  cc: string[];
  bcc: string[];
  attachments: string[] | null;
  refused: EmailRefusal[];
}

const refusalList = (value: unknown): EmailRefusal[] =>
  Array.isArray(value)
    ? value.flatMap((v): EmailRefusal[] => {
        if (!v || typeof v !== "object") return [];
        const { email, reason } = v as Record<string, unknown>;
        return typeof email === "string" && typeof reason === "string"
          ? [{ email, reason }]
          : [];
      })
    : [];

function parseSendEmailResult(
  text: string | undefined
): SendEmailResult | null {
  if (!text) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    return null;
  }
  if (!parsed || typeof parsed !== "object") return null;
  const r = parsed as Record<string, unknown>;
  if (typeof r.queued !== "boolean") return null;
  return {
    queued: r.queued,
    draftId: typeof r.draft_id === "string" ? r.draft_id : null,
    recipients: stringList(r.recipients),
    cc: stringList(r.cc),
    bcc: stringList(r.bcc),
    attachments: Array.isArray(r.attachments)
      ? stringList(r.attachments)
      : null,
    refused: refusalList(r.refused_recipients),
  };
}

// The SDK's subagent-dispatch tool name. A ToolSegment carrying it is a
// dispatched subagent: its own work nests in `children` (#10467) and it
// renders as a top-level row beside the work blocks (#11232).
export const SUBAGENT_TOOL_NAME = "Agent";

// The SDK's immediate background-launch acknowledgment for an async subagent
// (an "Agent"/Task tool dispatched to run in the background). It comes back as
// the Agent tool_result ~100ms after spawn and is NOT the subagent's real
// completion — the subagent then runs for however long it needs. Binding the
// node's "done" to this ack is the root cause of a background subagent showing
// finished the instant it starts (#10467). Detected by the SDK-authored ack
// text (there is no structured is-async flag today). Since #11117 the backend
// normalizes the ack's client copy to exactly this sentence (the raw ack
// carries internal metadata) — renaming this string breaks that pairing; its
// twin lives in fp/agent/conversation/redaction.py.
const ASYNC_LAUNCH_ACK_PREFIX = "Async agent launched successfully";
const isAsyncLaunchAck = (outputPreview?: string): boolean =>
  typeof outputPreview === "string" &&
  outputPreview.trimStart().startsWith(ASYNC_LAUNCH_ACK_PREFIX);

// Flip the tool segment with id `toolCallId` to done (only when still running,
// so an error status is preserved). Recurses into children so a nested
// subagent is reachable. Used when a background subagent emits its composed
// final answer — the real completion signal its launch-ack couldn't be
// (#10467).
function markToolDone(segments: Segment[], toolCallId: string): Segment[] {
  return segments.map((s) => {
    if (s.kind !== "tool") {
      return s;
    }
    if (s.id === toolCallId) {
      return s.status === "running" ? { ...s, status: "done" as const } : s;
    }
    if (s.children.length > 0) {
      return { ...s, children: markToolDone(s.children, toolCallId) };
    }
    return s;
  });
}

// Sweep any still-running tool (a background subagent whose completion never
// surfaced) to done when the turn is terminal — nothing is live once the turn
// ends, so no node should keep spinning (#10467).
function finalizeRunningTools(segments: Segment[]): Segment[] {
  return segments.map((s) => {
    if (s.kind !== "tool") {
      return s;
    }
    return {
      ...s,
      status: s.status === "running" ? "done" : s.status,
      children:
        s.children.length > 0 ? finalizeRunningTools(s.children) : s.children,
    };
  });
}

// Apply one segment-producing update to a flat Segment[] (no turn-level status).
// Shared by the top-level timeline and, for a dispatched subagent's events, by
// the parent tool segment's `children` list — so a subagent's work renders as
// its own nested timeline.
/** The build a build request's result names: its `build`, or the result itself as a fallback. */
function buildOf(data: unknown): { id?: unknown; title?: unknown } | null {
  if (!data || typeof data !== "object" || Array.isArray(data)) return null;
  const nested = (data as { build?: unknown }).build;
  return nested && typeof nested === "object" && !Array.isArray(nested)
    ? (nested as { id?: unknown; title?: unknown })
    : (data as { id?: unknown; title?: unknown });
}

/**
 * A build's ending can arrive before the result that names its build (#13380): the ending then
 * stood as a card of its own. Once the request's card knows the build, that card takes the ending
 * and the stand-alone card goes, so the chat shows one card per build.
 */
function foldEarlyBuildEnd(segments: Segment[]): Segment[] {
  const early = new Map<string, DashboardToolSegment>();
  for (const s of segments)
    if (s.kind === "dashboard" && s.id.startsWith("build-ended-") && s.buildId)
      early.set(s.buildId, s);
  if (early.size === 0) return segments;
  const taken = new Set<DashboardToolSegment>();
  const folded = segments.map((s) => {
    if (
      s.kind !== "dashboard" ||
      s.tool !== "build" ||
      !s.buildId ||
      s.id.startsWith("build-ended-")
    )
      return s;
    const ending = early.get(s.buildId);
    if (!ending) return s;
    taken.add(ending);
    return { ...s, ended: s.ended ?? ending.ended };
  });
  return folded.filter((s) => !(s.kind === "dashboard" && taken.has(s)));
}

/** Marks the build card for `buildId` as ending after its turn did. */
export function markBuildEndedAfterTurn(
  segments: Segment[],
  buildId: string
): Segment[] {
  return segments.map((s) =>
    s.kind === "dashboard" &&
    s.tool === "build" &&
    s.ended !== null &&
    s.ended.id === buildId
      ? { ...s, endedAfterTurn: true }
      : s
  );
}

/** A build ending the transcript shows at its end, and the instant it is dated by. */
export interface LateBuildEnding {
  seg: DashboardToolSegment;
  at: Date | null;
}

/** The build endings that came after their turns, in the order they were recorded. */
export function lateBuildEndings(
  turn: TurnState | undefined
): LateBuildEnding[] {
  return (turn?.segments ?? [])
    .filter(
      (s): s is DashboardToolSegment =>
        s.kind === "dashboard" && s.endedAfterTurn && s.ended !== null
    )
    .map((seg) => ({
      seg,
      at: seg.ended?.at ? new Date(seg.ended.at) : null,
    }));
}

/** What placing a late build ending reads of a transcript message. */
export interface PlacedMessage {
  isUser: boolean;
  timestamp: Date | string;
  thinkingTimes?: { start?: Date };
  turn?: TurnState;
}

/** When a message began: a reply by its turn's start, which a live and a reloaded reply agree on. */
const beganAt = (m: PlacedMessage): number =>
  new Date(
    !m.isUser && m.thinkingTimes?.start ? m.thinkingTimes.start : m.timestamp
  ).getTime();

/**
 * Where each build ending that came after its turn stands in the transcript (dashboard-assistant-
 * polish requirement 10): after the last message that began at or before the ending was recorded,
 * and never above its own turn. Keyed by the index of the message it follows, in recorded order.
 */
export function placeLateBuildEndings(
  messages: PlacedMessage[]
): Map<number, LateBuildEnding[]> {
  const placed = new Map<number, LateBuildEnding[]>();
  messages.forEach((owner, ownerIdx) => {
    for (const ending of lateBuildEndings(owner.turn)) {
      const arrived = ending.at?.getTime() ?? Infinity;
      let after = ownerIdx;
      for (let i = ownerIdx + 1; i < messages.length; i += 1) {
        if (beganAt(messages[i]) <= arrived) after = i;
      }
      placed.set(after, [...(placed.get(after) ?? []), ending]);
    }
  });
  for (const endings of placed.values())
    endings.sort(
      (a, b) => (a.at?.getTime() ?? Infinity) - (b.at?.getTime() ?? Infinity)
    );
  return placed;
}

/**
 * Where a host's dated line stands in the transcript (the dashboard assistant's restart line,
 * polish-8 R2): after the last message that began at or before it, as a late build ending stands;
 * -1 when it came before every message.
 */
export function placeTranscriptNotice(
  messages: PlacedMessage[],
  at: Date
): number {
  let after = -1;
  messages.forEach((message, index) => {
    if (beganAt(message) <= at.getTime()) after = index;
  });
  return after;
}

function applyToSegments(segments: Segment[], u: ChatUpdate): Segment[] {
  switch (u.type) {
    case "thinking": {
      const open = openSegment(segments);
      const finished = u.isComplete === true;
      if (open && open.kind === "thinking") {
        return replaceLast(segments, {
          ...open,
          text: u.text,
          status: finished ? "complete" : "streaming",
        });
      }
      const base = closeOpen(segments);
      const seg: ThinkingSegment = {
        kind: "thinking",
        id: `thinking-${base.length}`,
        text: u.text,
        status: finished ? "complete" : "streaming",
      };
      return [...base, seg];
    }

    case "streaming": {
      const open = openSegment(segments);
      if (open && open.kind === "text") {
        return replaceLast(segments, { ...open, text: u.text });
      }
      const base = closeOpen(segments);
      const seg: TextSegment = {
        kind: "text",
        id: `text-${base.length}`,
        text: u.text,
        status: "streaming",
      };
      return [...base, seg];
    }

    case "finalAnswer":
    case "contentWithCitations": {
      const chatSourceMap =
        u.type === "contentWithCitations" ? u.chatSourceMap : undefined;
      const eventId = u.type === "contentWithCitations" ? u.eventId : undefined;
      const open = openSegment(segments);
      if (open && open.kind === "text") {
        return replaceLast(segments, {
          ...open,
          text: u.text,
          chatSourceMap,
          eventId,
          status: "complete",
        });
      }
      const base = closeOpen(segments);
      const seg: TextSegment = {
        kind: "text",
        id: `text-${base.length}`,
        text: u.text,
        chatSourceMap,
        eventId,
        status: "complete",
      };
      return [...base, seg];
    }

    case "clarifyingQuestion": {
      // Closes the block before it: the agent stopped working to ask.
      const base = closeOpen(segments);
      const seg: ClarifyingQuestionSegment = {
        kind: "clarifyingQuestion",
        id: u.eventId,
        eventId: u.eventId,
        question: u.question,
        toolUseId: u.toolUseId,
      };
      return [...base, seg];
    }

    case "outputs": {
      // Produced files close any open block and stand on their own, after the
      // answer text that describes them.
      const seg: OutputsSegment = {
        kind: "outputs",
        id: `outputs-${u.messageId}`,
        conversationId: u.conversationId,
        turnId: u.messageId,
        files: u.files,
      };
      return [...closeOpen(segments), seg];
    }

    case "memoryUpdated": {
      // One notice per turn, kept at the end: a second notice (the tool
      // wrote during the turn, then the curation pass wrote after it) or a
      // repeat (a live frame followed by the reload replay) merges its
      // subjects into the one line instead of adding another.
      const closed = closeOpen(segments);
      const index = closed.findIndex((s) => s.kind === "memoryUpdated");
      const previous = index >= 0 ? closed[index] : null;
      const before =
        previous && previous.kind === "memoryUpdated" ? previous : null;
      const merged = before
        ? Array.from(new Set([...before.subjects, ...u.subjects]))
        : u.subjects;
      // Files union by name, refused files by path (#11402).
      const files = Array.from(
        new Set([...(before?.files ?? []), ...(u.files ?? [])])
      );
      const refused = [...(before?.refused ?? [])];
      for (const entry of u.refused ?? []) {
        if (!refused.some((r) => r.path === entry.path)) refused.push(entry);
      }
      const seg: MemoryUpdatedSegment = {
        kind: "memoryUpdated",
        id: `memory-${u.messageId}`,
        subjects: merged,
        files,
        refused,
      };
      const rest =
        index >= 0
          ? [...closed.slice(0, index), ...closed.slice(index + 1)]
          : closed;
      return [...rest, seg];
    }

    case "dashboardBuildEnded": {
      // The build's card reads its ending; a build whose card this turn does
      // not hold (an older request) gets a card of its own. Folded by build
      // id, so the live frame and the reload's replay make one card.
      const card = segments.find(
        (s): s is DashboardToolSegment =>
          s.kind === "dashboard" &&
          s.tool === "build" &&
          (s.buildId === u.build.id || s.id === `build-ended-${u.build.id}`)
      );
      if (card)
        return segments.map((s) =>
          s === card
            ? { ...card, ended: u.build, text: card.text || u.build.title }
            : s
        );
      const seg: DashboardToolSegment = {
        kind: "dashboard",
        id: `build-ended-${u.build.id}`,
        toolCallId: `build-ended-${u.build.id}`,
        tool: "build",
        text: u.build.title,
        outcome: null,
        term: "",
        matches: [],
        buildId: u.build.id,
        ended: u.build,
        endedAfterTurn: false,
        revision: false,
        pending: false,
      };
      return [...closeOpen(segments), seg];
    }

    case "turnNotice": {
      // Closes whatever was open and stands on its own line, in arrival
      // order: each extra pass gets one, so two notices are two lines.
      const base = closeOpen(segments);
      const seg: TurnNoticeSegment = {
        kind: "turnNotice",
        id: `notice-${base.length}`,
        code: u.code,
      };
      return [...base, seg];
    }

    case "thinkingNote": {
      // The note is the durable form of the thinking run that just ended: it
      // REPLACES a trailing streaming thinking segment (live), or simply
      // appends (reload, where deltas never existed).
      const open = openSegment(segments);
      const seg: NoteSegment = {
        kind: "note",
        id: `note-${segments.length}`,
        text: u.text,
      };
      if (open && open.kind === "thinking") {
        return replaceLast(segments, seg);
      }
      return [...closeOpen(segments), seg];
    }

    case "toolCall": {
      const base = closeOpen(segments);
      const seg: ToolSegment = {
        kind: "tool",
        id: u.step.id,
        toolCallId: u.step.id,
        toolName: u.step.toolName,
        input: u.step.input,
        status: "running",
        displayVerb: u.step.displayVerb,
        children: [],
      };
      if (isEditorDocumentTool(u.step.toolName)) {
        const doc: DocumentSegment = {
          kind: "document",
          id: `doc-${u.step.id}`,
          toolCallId: u.step.id,
          workspaceId: null,
          title:
            typeof u.step.input?.title === "string" ? u.step.input.title : null,
          // Painted only once its tool_result confirms success (#10501's
          // chart discipline, same reason).
          pending: true,
        };
        return [...base, seg, doc];
      }
      if (isUpdateEditorDocumentTool(u.step.toolName)) {
        // A turn that only writes into an existing document still gets a
        // card to reopen it from (#12139) — unless this turn already holds
        // one for the same workspace (its create, or an earlier update), in
        // which case that card stands for every write. The update's input
        // carries no title, so the card wears the view's generic label.
        const workspaceId = u.step.input?.workspace_id;
        const alreadyCarded =
          typeof workspaceId === "string" &&
          base.some(
            (s) => s.kind === "document" && s.workspaceId === workspaceId
          );
        if (typeof workspaceId !== "string" || alreadyCarded) {
          return [...base, seg];
        }
        // The id is known from the input, so the card carries it from the
        // start; it still paints only once the result confirms the write.
        const doc: DocumentSegment = {
          kind: "document",
          id: `doc-${u.step.id}`,
          toolCallId: u.step.id,
          workspaceId,
          title: null,
          pending: true,
        };
        return [...base, seg, doc];
      }
      if (isCreateTableTool(u.step.toolName)) {
        const table: TableArtifactSegment = {
          kind: "tableArtifact",
          id: `tbl-${u.step.id}`,
          toolCallId: u.step.id,
          artifactId: null,
          title:
            typeof u.step.input?.title === "string" ? u.step.input.title : null,
          pending: true,
        };
        return [...base, seg, table];
      }
      if (isCompleteTaskTool(u.step.toolName)) {
        const handoff: HandoffSegment = {
          kind: "handoff",
          id: `handoff-${u.step.id}`,
          toolCallId: u.step.id,
          result: null,
          pending: true,
        };
        return [...base, seg, handoff];
      }
      const taskTool = taskToolKind(u.step.toolName);
      if (taskTool && taskTool !== "list") {
        const task: TaskSegment = {
          kind: "task",
          id: `task-${u.step.id}`,
          toolCallId: u.step.id,
          tool: taskTool,
          inputTitle:
            typeof u.step.input?.title === "string" ? u.step.input.title : null,
          result: null,
          pending: true,
        };
        return [...base, seg, task];
      }
      if (isSendEmailTool(u.step.toolName)) {
        const input = u.step.input ?? {};
        const email: EmailSegment = {
          kind: "email",
          id: `email-${u.step.id}`,
          toolCallId: u.step.id,
          to: stringList(input.to),
          subject: typeof input.subject === "string" ? input.subject : "",
          body: typeof input.body === "string" ? input.body : "",
          attachments: stringList(input.attachments),
          draftId: null,
          queued: false,
          recipients: [],
          cc: stringList(input.cc),
          bcc: stringList(input.bcc),
          refused: [],
          // Painted only once its tool_result says the call was accepted: a
          // refused call never drafted anything to show.
          pending: true,
        };
        return [...base, seg, email];
      }
      const dashboardTool = dashboardToolKind(u.step.toolName);
      if (dashboardTool) {
        const input = u.step.input ?? {};
        const card: DashboardToolSegment = {
          kind: "dashboard",
          id: `dashboard-${u.step.id}`,
          toolCallId: u.step.id,
          tool: dashboardTool,
          text: dashboardTool === "build" ? textOf(input.title) : "",
          outcome: null,
          term: textOf(input.query),
          matches: [],
          buildId: null,
          ended: null,
          endedAfterTurn: false,
          revision:
            bareToolName(u.step.toolName ?? "") === "request_widget_revision",
          // Painted once its result says the call did what it asked.
          pending: true,
        };
        return [...base, seg, card];
      }
      return [...base, seg];
    }

    case "toolResult": {
      // A create_chart / create_diagram segment is appended `pending` on its
      // streamed tool_call and paints only once this result confirms success:
      // an errored result REMOVES it (a rejected call never paints, and the
      // model's retry can't leave a duplicate), a successful one CLEARS
      // `pending` so it paints (#10501).
      // A document segment resolves here too: a successful result carries
      // the created workspace id in its preview; without one there is
      // nothing to reopen, so the segment is dropped alongside the errored
      // chart/diagram cases.
      const resultWorkspaceId = u.error
        ? null
        : workspaceIdFromPreview(u.outputPreview);
      const resultArtifactId = u.error
        ? null
        : artifactIdFromPreview(u.outputPreview);
      // A task card resolves the same way (#11942): the result's preview
      // names the task it acted on, or the card is dropped.
      const taskSeg = segments.find(
        (s): s is TaskSegment =>
          s.kind === "task" && s.toolCallId === u.toolCallId
      );
      // The structured result, when the backend carried it, is the whole
      // return; the preview is the fallback for rows written before it.
      const taskResult =
        taskSeg && !u.error
          ? parseTaskToolResult(
              taskSeg.tool,
              u.data !== undefined ? JSON.stringify(u.data) : u.outputPreview,
              taskSeg.inputTitle
            )
          : null;
      const handoffResult = u.error ? null : handoffFromData(u.data);
      // An email segment resolves the same way (#12210): the result says
      // draft or queued and names the addresses; a refusal, or a result that
      // is not the tool's JSON, leaves nothing to show.
      const emailResult = u.error
        ? null
        : parseSendEmailResult(
            // The whole return when the backend carried it (`data`, which
            // survives a reload), else the live frame's full text, else the
            // preview, which can be cut short.
            u.data !== undefined
              ? JSON.stringify(u.data)
              : (u.outputContent ?? u.outputPreview)
          );
      const pruned = segments.filter(
        (s) =>
          !(
            (u.error &&
              ((s.kind === "chart" && s.chartId === u.toolCallId) ||
                (s.kind === "diagram" && s.diagramId === u.toolCallId))) ||
            (s.kind === "document" &&
              s.toolCallId === u.toolCallId &&
              resultWorkspaceId === null) ||
            (s.kind === "tableArtifact" &&
              s.toolCallId === u.toolCallId &&
              resultArtifactId === null) ||
            (s.kind === "task" &&
              s.toolCallId === u.toolCallId &&
              taskResult === null) ||
            (s.kind === "handoff" &&
              s.toolCallId === u.toolCallId &&
              handoffResult === null) ||
            (s.kind === "email" &&
              s.toolCallId === u.toolCallId &&
              emailResult === null) ||
            (s.kind === "dashboard" &&
              s.toolCallId === u.toolCallId &&
              u.error !== undefined)
          )
      );
      return foldEarlyBuildEnd(
        pruned.map((s) => {
          if (s.kind === "dashboard" && s.toolCallId === u.toolCallId) {
            const call = segments.find(
              (t): t is ToolSegment =>
                t.kind === "tool" && t.toolCallId === u.toolCallId
            );
            // A build request answers `{build, draft_version}` (#13380); a bare build is read too.
            const built = buildOf(u.data);
            const build = textOf(built?.title);
            return {
              ...s,
              text:
                s.tool === "ops"
                  ? opsOutcome(call?.input, u.outputPreview, u.data)
                  : s.tool === "build"
                    ? build || s.text
                    : previewWords(u.outputPreview),
              outcome: s.tool === "ops" ? opsAnswer(u.data) : null,
              matches: s.tool === "search" ? matchesOf(u.data) : [],
              buildId:
                s.tool === "build" && built
                  ? textOf(built.id) || s.buildId
                  : s.buildId,
              pending: false,
            };
          }
          if (
            s.kind === "task" &&
            s.toolCallId === u.toolCallId &&
            taskResult !== null
          ) {
            return { ...s, result: taskResult, pending: false };
          }
          if (
            s.kind === "handoff" &&
            s.toolCallId === u.toolCallId &&
            handoffResult !== null
          ) {
            return { ...s, result: handoffResult, pending: false };
          }
          if (
            s.kind === "email" &&
            s.toolCallId === u.toolCallId &&
            emailResult !== null
          ) {
            return {
              ...s,
              draftId: emailResult.draftId,
              queued: emailResult.queued,
              recipients: emailResult.recipients,
              cc: emailResult.cc,
              bcc: emailResult.bcc,
              attachments: emailResult.attachments ?? s.attachments,
              refused: emailResult.refused,
              pending: false,
            };
          }
          if (!u.error && s.kind === "chart" && s.chartId === u.toolCallId) {
            return { ...s, pending: false };
          }
          if (
            !u.error &&
            s.kind === "diagram" &&
            s.diagramId === u.toolCallId
          ) {
            return { ...s, pending: false };
          }
          if (
            s.kind === "document" &&
            s.toolCallId === u.toolCallId &&
            resultWorkspaceId !== null
          ) {
            return { ...s, workspaceId: resultWorkspaceId, pending: false };
          }
          if (
            s.kind === "tableArtifact" &&
            s.toolCallId === u.toolCallId &&
            resultArtifactId !== null
          ) {
            return { ...s, artifactId: resultArtifactId, pending: false };
          }
          if (s.kind !== "tool" || s.toolCallId !== u.toolCallId) {
            return s;
          }
          // A background subagent's tool_result is the SDK launch-ack, not its
          // completion — keep it running (it finishes on its own parented final
          // answer, or is swept done at turn end) and never store the internal
          // ack text so it can't render (#10467). Inline/blocking subagents and
          // every other tool still complete on their result as before.
          if (
            s.toolName === SUBAGENT_TOOL_NAME &&
            isAsyncLaunchAck(u.outputPreview)
          ) {
            return s;
          }
          return {
            ...s,
            status: u.error ? "error" : "done",
            outputPreview: u.outputPreview,
            error: u.error,
            durationMs: u.durationMs,
          };
        })
      );
    }

    case "chart": {
      const base = closeOpen(segments);
      const seg: ChartSegment = {
        kind: "chart",
        id: u.chartId,
        chartId: u.chartId,
        spec: u.spec,
        title: u.title,
        sourceIds: u.sourceIds,
        // Painted only once its tool_result confirms success (#10501).
        pending: true,
      };
      return [...base, seg];
    }

    case "diagram": {
      const base = closeOpen(segments);
      const seg: DiagramSegment = {
        kind: "diagram",
        id: u.diagramId,
        diagramId: u.diagramId,
        diagram: u.diagram,
        title: u.title,
        sourceIds: u.sourceIds,
        // Painted only once its tool_result confirms success (#10501).
        pending: true,
      };
      return [...base, seg];
    }

    default:
      // status / error / title / deleted are turn-level — handled by the caller.
      return segments;
  }
}

// Route a subagent's update into the `children` of the tool segment whose id is
// `parentId` (the dispatching Task call). Recurses so a subagent that itself
// dispatches a subagent still lands in the right place.
function nestUpdate(
  segments: Segment[],
  parentId: string,
  u: ChatUpdate
): Segment[] {
  return segments.map((s) => {
    if (s.kind !== "tool") {
      return s;
    }
    if (s.id === parentId) {
      return { ...s, children: applyToSegments(s.children, u) };
    }
    if (s.children.length > 0) {
      return { ...s, children: nestUpdate(s.children, parentId, u) };
    }
    return s;
  });
}

/** Pin a turn to a terminal status through the same close-out a live terminal
 * event takes: the trailing open text/thinking block is completed and
 * still-running tools are swept to done (nothing is live once the turn ends,
 * #10467). The ONLY way to end a turn — both reload paths (chat's
 * foldEventsToMessages and projects' buildTranscript) close out through here.
 * Never spread a terminal status onto a TurnState instead: a raw
 * `{ ...turn, status }` skips the sweep, so a persisted turn whose last
 * tool_call has no tool_result keeps rendering a running tool forever. */
export function closeOutTurn(
  turn: TurnState,
  status: Exclude<TurnStatus, "running">
): TurnState {
  return {
    ...turn,
    status,
    segments: finalizeRunningTools(closeOpen(turn.segments)),
  };
}

export function applyChatUpdate(turn: TurnState, u: ChatUpdate): TurnState {
  switch (u.type) {
    case "status": {
      if (u.status === "started") {
        return { ...turn, status: "running" };
      }
      // Terminal: close out through the shared path (open block finalized,
      // running tools swept) and set the turn status.
      const status: Exclude<TurnStatus, "running"> =
        u.status === "completed"
          ? "completed"
          : u.status === "failed"
            ? "failed"
            : "canceled";
      return closeOutTurn(turn, status);
    }

    case "error":
      return { ...closeOutTurn(turn, "failed"), errorMessage: u.error };

    // Turn-level metadata, not segments: a work block's header (#10396 —
    // one per block) plus, on legacy #10382 turns, activity lines. The
    // header replaces in place; activity accumulates, dropping exact
    // repeats.
    case "statusGist": {
      const blockHeaders = { ...turn.blockHeaders, [u.block]: u.text };
      let blockActivity = turn.blockActivity;
      if (u.steps && u.steps.length > 0) {
        const target = u.stepsBlock ?? u.block;
        const merged = [...(blockActivity[target] ?? [])];
        for (const line of u.steps) {
          if (!merged.includes(line)) {
            merged.push(line);
          }
        }
        blockActivity = { ...blockActivity, [target]: merged };
      }
      return { ...turn, blockHeaders, blockActivity };
    }

    // Conversation-scoped or out-of-band — not part of the turn timeline.
    // usage carries token totals the transcript view reads directly; it
    // produces no segment and has no parentToolCallId for the default arm.
    case "title":
    case "deleted":
    case "usage":
      return turn;

    case "dashboardBuildEnded": {
      // An ending that arrives while its turn is open folds into the turn's
      // card, which is already at the end; one that arrives after the turn
      // ended is marked so, and the transcript shows it at its end
      // (dashboard-assistant-polish requirement 10).
      const segments = applyToSegments(turn.segments, u);
      if (turn.status === "running") return { ...turn, segments };
      return {
        ...turn,
        segments: markBuildEndedAfterTurn(segments, u.build.id),
      };
    }

    case "outputs":
    case "memoryUpdated":
    case "turnNotice":
      // Produced files and the turn's notices belong to the turn, not to a
      // dispatched subagent's Task call, so they never nest — go straight to
      // the segment list.
      return { ...turn, segments: applyToSegments(turn.segments, u) };

    default: {
      // A segment-producing update. When it carries a parent tool-call id it
      // was made by a dispatched subagent — nest it under that Task call.
      const parentId = u.parentToolCallId;
      if (!parentId) {
        return { ...turn, segments: applyToSegments(turn.segments, u) };
      }
      let segments = nestUpdate(turn.segments, parentId, u);
      // A background subagent's composed final answer is its real completion
      // signal — the async launch-ack couldn't be — so it marks the parent
      // Agent node done (#10467). Inline subagents already went done on their
      // result; markToolDone is a no-op once a node isn't running.
      if (u.type === "finalAnswer" || u.type === "contentWithCitations") {
        segments = markToolDone(segments, parentId);
      }
      return { ...turn, segments };
    }
  }
}

// Fold a full event stream into a turn. Used live (accumulating) and on reload
// (replaying the persisted, delta-free event log).
export function buildTurn(updates: ChatUpdate[]): TurnState {
  return updates.reduce(applyChatUpdate, emptyTurn());
}

// The number of background subagents (Agent tool calls) still running, derived
// from segment state rather than the opaque task_* lifecycle events. Drives
// the timeline's "Research in progress…" state (#11232) so the
// main-agent-idle phase (waiting on background tasks) is not dead-air
// (#10467). Recurses so a subagent that itself dispatches subagents is
// counted.
export function runningSubagentCount(segments: Segment[]): number {
  return segments.reduce((n, s) => {
    if (s.kind !== "tool") {
      return n;
    }
    const self =
      s.toolName === SUBAGENT_TOOL_NAME && s.status === "running" ? 1 : 0;
    return n + self + runningSubagentCount(s.children);
  }, 0);
}

// Whether any email card stands in these segments, including a dispatched
// subagent's (#12210). The agents page reads the session's emails only when
// there is a card to keep live.
export function hasEmailSegment(segments: Segment[]): boolean {
  return segments.some(
    (s) =>
      s.kind === "email" || (s.kind === "tool" && hasEmailSegment(s.children))
  );
}

// The plain-text answer for a turn: its text segments joined in order. Used for
// the copy-to-clipboard action (the timeline has no single content string).
export function turnAnswerText(turn: TurnState | undefined): string {
  return (turn?.segments ?? [])
    .filter((s): s is TextSegment => s.kind === "text")
    .map((s) => s.text)
    .join("\n\n");
}

// ------------------------------------------------------------ handoff --

/** The complete_task tool, by its bare suffix (the name arrives prefixed). */
export const isCompleteTaskTool = (toolName: string | undefined): boolean =>
  (toolName?.split("__").pop() ?? "") === "complete_task";

const str = (v: unknown): string | null => (typeof v === "string" ? v : null);
const obj = (v: unknown): Record<string, unknown> | null =>
  v && typeof v === "object" && !Array.isArray(v)
    ? (v as Record<string, unknown>)
    : null;

/** A handoff from complete_task's structured result: the task that opened
 * next when it is a person's (or a role's, not yet assigned). Null when the
 * result has no data, when the next task is the agent's own or the
 * system's, or when the list finished. */
export const handoffFromData = (data: unknown): HandoffResult | null => {
  const body = obj(data);
  const n = body ? obj(body.next) : null;
  if (!body || !n) return null;
  if (n.waiting_on !== "person" && n.waiting_on !== "nobody") return null;
  const w = obj(n.waiting_task);
  if (!w) return null;
  const taskId = str(w.task_id);
  const taskTitle = str(w.title);
  if (!taskId || !taskTitle) return null;
  const assignee = obj(w.assignee);
  const done = obj(body.task);
  const opened = obj(n.opened_step);
  return {
    doneTitle: done ? str(done.title) : null,
    taskId,
    taskTitle,
    assigneeName: assignee ? str(assignee.name) : null,
    action: w.action === "approve" ? "approve" : "mark_done",
    stepPosition:
      opened && typeof opened.position === "number" ? opened.position : null,
    listId: str(w.list_id),
  };
};
